import _ from 'lodash'

/**
 * Utilities para manipulação de módulos
 */

function uniqueIds(values) {
    return [
        ...new Set(
            values
                .filter(value => value != null && value !== '')
                .map(value => String(value)),
        ),
    ]
}

function idsOverlap(idsA, idsB) {
    return (
        idsA.length > 0 && idsB.length > 0 && idsA.some(id => idsB.includes(id))
    )
}

export function getModuleIdentifierValues(module) {
    if (!module) return []

    return uniqueIds([
        module.module_id,
        module.id,
        module.external_id,
        module.module && module.module.id,
        module.module && module.module.external_id,
    ])
}

export function modulesReferToSameModule(moduleA, moduleB) {
    return idsOverlap(
        getModuleIdentifierValues(moduleA),
        getModuleIdentifierValues(moduleB),
    )
}

function getInternalModuleIds(module, { isPackageModule = false } = {}) {
    if (!module) return []

    const ids = [module.module_id, module.module && module.module.id]

    // No pacote, module.id é a PK da linha — não misturar com o id do módulo.
    if (!isPackageModule) {
        ids.push(module.id)
    }

    return uniqueIds(ids)
}

function getExternalModuleIds(module) {
    if (!module) return []

    return uniqueIds([
        module.module && module.module.external_id,
        module.external_id,
    ])
}

export function customerRecordMatchesPackageModule(record, packageModule) {
    if (!record || !packageModule) return false

    if (
        idsOverlap(
            getInternalModuleIds(record),
            getInternalModuleIds(packageModule, { isPackageModule: true }),
        )
    ) {
        return true
    }

    return idsOverlap(
        getExternalModuleIds(record),
        uniqueIds([packageModule.module && packageModule.module.external_id]),
    )
}

export function findMatchingFixedModule(fixedModules, module) {
    if (!Array.isArray(fixedModules) || !module) return null

    const moduleIds = getInternalModuleIds(module, { isPackageModule: true })
    if (!moduleIds.length) return null

    return (
        fixedModules.find(fixedModule =>
            idsOverlap(
                getInternalModuleIds(fixedModule, { isPackageModule: true }),
                moduleIds,
            ),
        ) || null
    )
}

function findCustomerRecords(activeModuleIds, packageModule) {
    if (!Array.isArray(activeModuleIds) || !packageModule) return []

    return activeModuleIds.filter(record =>
        customerRecordMatchesPackageModule(record, packageModule),
    )
}

function recordAmount(record) {
    const amount = Number(record && record.amount)
    return Number.isFinite(amount) && amount > 0 ? amount : 0
}

/**
 * Prefill do opcional com o plano atual do cliente.
 * Extra = 2ª ocorrência / add_on, ou quantidade acima da inclusa no pacote.
 */
export function getOptionalPrefillFromCustomer(
    activeModuleIds,
    packageModule,
    fixedModules,
) {
    const matches = findCustomerRecords(activeModuleIds, packageModule)
    if (!matches.length) return null

    const included = findMatchingFixedModule(fixedModules, packageModule)
    const includedAmount = included ? recordAmount(included) : 0

    const addon =
        matches.find(record => record.add_on === true) ||
        (matches.length > 1 ? matches[1] : null)

    if (addon) {
        const extraAmount = recordAmount(addon) || 1
        return { ...addon, amount: extraAmount }
    }

    const summed = matches.reduce(
        (sum, record) => sum + recordAmount(record),
        0,
    )
    const customerAmount = summed > 0 ? summed : matches.length
    const extra = customerAmount - includedAmount

    if (extra <= 0) return null

    return { ...matches[0], amount: extra }
}

/**
 * Encontra o índice de um módulo na lista de módulos selecionados
 * @param {Array} modules - Lista de módulos
 * @param {Number|Object} moduleOrId - ID do módulo ou objeto para match por identificadores
 * @returns {Number} Índice do módulo (-1 se não encontrado)
 */
export function findModuleIndex(modules, moduleOrId) {
    if (typeof moduleOrId !== 'object') {
        return modules.findIndex(module => module.module_id === moduleOrId)
    }

    return modules.findIndex(module =>
        modulesReferToSameModule(module, moduleOrId),
    )
}

/**
 * Verifica se um módulo existe na lista
 * @param {Array} modules - Lista de módulos
 * @param {Number} moduleId - ID do módulo a buscar
 * @returns {Boolean}
 */
export function moduleExists(modules, moduleId) {
    return findModuleIndex(modules, moduleId) !== -1
}

/**
 * Verifica se um módulo tem combos disponíveis
 * @param {Object} module - Módulo a verificar
 * @returns {Boolean}
 */
export function hasCombo(module) {
    return (
        module.combo && Array.isArray(module.combo) && module.combo.length > 0
    )
}

/**
 * Verifica se um módulo tem dependências
 * @param {Object} module - Módulo a verificar
 * @returns {Boolean}
 */
export function hasDependency(module) {
    return (
        module.dependency &&
        Array.isArray(module.dependency) &&
        module.dependency.length > 0
    )
}

/**
 * Verifica se um módulo tem cortesias
 * @param {Object} module - Módulo a verificar
 * @returns {Boolean}
 */
export function hasCourtesy(module) {
    return (
        module.courtesy &&
        Array.isArray(module.courtesy) &&
        module.courtesy.length > 0
    )
}

/**
 * Filtra módulos fixos de um pacote
 * @param {Array} modules - Módulos do pacote
 * @returns {Array} Módulos fixos ordenados
 */
export function filterFixedModules(modules) {
    return _.sortBy(
        modules.filter(
            module =>
                !module.add_on &&
                (module.amount == null ||
                    typeof module.amount === 'undefined' ||
                    module.amount > 0),
        ),
        'order',
    )
}

/**
 * Filtra módulos opcionais de um pacote
 * @param {Array} packageModules - Módulos do pacote
 * @param {Array} comboItems - Itens de combo
 * @returns {Array} Módulos opcionais ordenados
 */
export function filterOptionalModules(packageModules, comboItems = []) {
    const packageItems = packageModules.filter(
        module => module.add_on || module.max_amount > module.amount,
    )

    const uniqueComboItems = comboItems.filter(
        comboItem =>
            !packageItems.some(pkgModule =>
                modulesReferToSameModule(pkgModule, comboItem),
            ),
    )

    return _.sortBy(
        [...packageItems, ...uniqueComboItems],
        ['price_type', 'order'],
    )
}

/**
 * Filtra módulos selecionados (checked)
 * @param {Array} modules - Lista de módulos
 * @returns {Array} Módulos selecionados ordenados
 */
export function filterSelectedModules(modules) {
    return _.sortBy(
        modules.filter(module => module.checked),
        'order',
    )
}

/**
 * Cria objeto de módulo para proposta
 * @param {Object} module - Módulo original
 * @returns {Object} Objeto formatado para proposta
 */
export function createProposalModule(module) {
    return {
        module_id: module.module_id,
        external_id: module.module.external_id,
        name: module.module.name,
        amount:
            module.add_amount != null
                ? module.amount + module.add_amount
                : module.amount,
        price: module.price,
        price_type: module.price_type,
        add_amount: module.add_amount,
        checked: module.checked,
        locked: module.locked || false,
        third_party: module.module.third_party || false,
    }
}

/**
 * Remove itens de um array por índices (em ordem reversa)
 * @param {Array} array - Array a modificar
 * @param {Array} indices - Índices a remover
 * @returns {Array} Array modificado
 */
export function removeByIndices(array, indices) {
    indices
        .sort((a, b) => b - a)
        .forEach(index => {
            array.splice(index, 1)
        })
    return array
}
